import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Skeleton, Typography } from '@mui/material';
import Link from 'next/link';
import { Fragment, useId } from 'react';

import { useBatchAccountActivity } from '../../hooks/useBatchTrackingQueries';
import { RoutePaths } from '../../libs';
import type { Kitchen } from '../../libs';
import { AccountAvatarGroup } from '../BatchTracking/AccountAvatar';
import { bucketTotal, currentHourIndex, latestRecording } from '../BatchTracking/activityStats';
import { cardSx, ease, fromTablet, mobileOnly, REDUCED_MOTION, shadow, tones } from '../BatchTracking/dashboardTokens';
import { formatTime } from '../BatchTracking/format';
import { strings } from '../BatchTracking/strings';
import type { BatchAccountActivity } from '../BatchTracking/types';
import { focusRing } from '../chefStyles';

const SPARK_MIN_HEIGHT = 12;
const SPARK_TICK_HEIGHT = 6;
const FULL = 100;

const onlyFromTablet = { display: 'none', [fromTablet]: { display: 'flex' } } as const;

const Sparkline = ({ activity }: { activity: BatchAccountActivity }) => {
  const { trend } = activity;
  const peak = Math.max(1, ...trend.map(bucketTotal));
  const now = currentHourIndex(trend, activity.bucketSize, activity.asOf);
  return (
    <Box aria-hidden sx={{ ...onlyFromTablet, alignItems: 'flex-end', gap: '2px', width: 132, height: 36, flex: 'none' }}>
      {trend.map((bucket, index) => {
        const total = bucketTotal(bucket);
        return (
          <Box
            key={bucket.start}
            sx={{
              flex: 1,
              height: `${total > 0 ? Math.max(SPARK_MIN_HEIGHT, (total / peak) * FULL) : SPARK_TICK_HEIGHT}%`,
              borderRadius: '2px 2px 0 0',
              bgcolor: total === 0 ? 'neutral100' : index === now ? 'primary600' : 'primary300'
            }}
          />
        );
      })}
    </Box>
  );
};

/** The summary sentence with its two numbers in bold. */
const Sentence = ({ text }: { text: string }) => (
  <>
    {text.split(/(\d+)/).map((part, index) => (
      <Fragment key={index}>
        {index % 2 === 1 ? (
          <Box component="b" sx={{ fontWeight: 700, color: 'primary800' }}>
            {part}
          </Box>
        ) : (
          part
        )}
      </Fragment>
    ))}
  </>
);

export interface AccountActivityCardViewProps {
  kitchen: Kitchen;
  /** Unset until a read has landed; the card never states a count it does not have. */
  activity: BatchAccountActivity | undefined;
  stale: boolean;
}

/** One link to the Accounts page, with today's count and the hours the batches were recorded in. */
export function AccountActivityCardView({ kitchen, activity, stale }: AccountActivityCardViewProps) {
  const staleId = useId();
  const showStale = stale && activity !== undefined;
  const sentence = activity
    ? strings.accounts.homeCard({ accounts: activity.summary.recordingAccounts, batches: activity.summary.user.rootBatches })
    : undefined;
  const lastRecorded = activity && latestRecording(activity.accounts);
  const updated = activity && formatTime(activity.asOf, kitchen);
  return (
    <Box
      component={Link}
      href={RoutePaths.batchTrackingAccounts}
      aria-label={sentence}
      aria-describedby={showStale ? staleId : undefined}
      sx={{
        ...cardSx,
        display: 'flex',
        alignItems: 'center',
        columnGap: 2,
        mt: 2.5,
        py: 2,
        pr: 2,
        pl: 2.25,
        color: 'neutral900',
        textDecoration: 'none',
        transition: `box-shadow 200ms ${ease}, border-color 200ms ${ease}, transform 200ms ${ease}`,
        '&:hover, &:focus-visible': { boxShadow: shadow.raised, borderColor: 'primary200', transform: 'translateY(-1px)' },
        '&:focus-visible': focusRing,
        [`@media ${REDUCED_MOTION}`]: { transition: 'none', '&:hover, &:focus-visible': { transform: 'none' } },
        [mobileOnly]: { mt: 2, columnGap: 1.5, py: 1.75, pr: 1.5, pl: 1.75 }
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 44,
          height: 44,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '12px',
          bgcolor: tones.green.bg,
          color: 'primary700',
          [mobileOnly]: { width: 40, height: 40 }
        }}
      >
        <PeopleAltOutlinedIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 16,
            lineHeight: '24px',
            fontWeight: 500,
            textWrap: 'pretty',
            [mobileOnly]: { fontSize: 15, lineHeight: '22px' }
          }}
        >
          {sentence ? <Sentence text={sentence} /> : strings.accounts.homeCardTitle}
        </Typography>
        {showStale && (
          <Typography
            id={staleId}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 13,
              lineHeight: '18px',
              color: 'secondaryYellow800'
            }}
          >
            <WarningAmberRoundedIcon aria-hidden sx={{ fontSize: 16, color: 'secondaryYellow700' }} />
            {strings.accounts.stale(updated ?? '')}
          </Typography>
        )}
        {!showStale && updated && (
          <Typography component="span" sx={{ fontSize: 13, lineHeight: '18px', color: 'neutral800' }}>
            {lastRecorded
              ? strings.accounts.homeMeta(formatTime(lastRecorded, kitchen), updated)
              : strings.accounts.updated(updated)}
          </Typography>
        )}
      </Box>
      {activity ? (
        <>
          <Sparkline activity={activity} />
          <Box aria-hidden sx={{ ...onlyFromTablet, flex: 'none' }}>
            {activity.accounts.length > 0 && <AccountAvatarGroup accounts={activity.accounts} max={4} />}
          </Box>
        </>
      ) : (
        <Skeleton
          aria-hidden
          variant="rounded"
          animation="wave"
          sx={{
            display: 'none',
            [fromTablet]: { display: 'block' },
            width: 132,
            height: 36,
            flex: 'none',
            borderRadius: '8px',
            bgcolor: 'neutral100'
          }}
        />
      )}
      <ChevronRightIcon aria-hidden sx={{ flex: 'none', color: 'neutral800' }} />
    </Box>
  );
}

/** Reads today's activity of the kitchen. A read that has not arrived, or failed, leaves the title alone. */
const AccountActivityCard = ({ kitchen, today }: { kitchen: Kitchen; today: string }) => {
  const { activity, stale } = useBatchAccountActivity(kitchen, { from: today, to: today });
  return <AccountActivityCardView kitchen={kitchen} activity={activity} stale={stale} />;
};

export default AccountActivityCard;
